
import { Component, Vue, Prop } from 'vue-property-decorator'
import OzContainedButton, {
  OzContainedButtonColorScheme,
  OzContainedButtonSizePreset,
  OzContainedButtonHrefMode,
} from 'library/v4/components/OzContainedButton.vue'
import OzIcon from 'library/v4/components/OzIcon.vue'

/**
 * Icon-only button. A shortcut for using OzContainedButton with only an icon.
 * Allows using a different icon for active state and automatically paints it
 * using selection color.
 */
@Component({
  components: {
    OzContainedButton,
    OzIcon,
  },
})
export default class OzIconButton extends Vue {
  $el!: OzContainedButton['$el']

  ColorScheme = OzContainedButtonColorScheme
  SizePreset = OzContainedButtonSizePreset

  /**
   * Text label for the button.
   */
  @Prop({ required: true }) label!: string

  /**
   * Icon name.
   */
  @Prop({ required: true }) iconName!: string

  /**
   * Determines the icon font to load
   * @values immaterial, oricon
   */
  @Prop({
    type: String,
    required: false,
    default: 'oricon',
    validator: (value) => ['immaterial', 'oricon'].includes(value as string),
  })
  iconFont!: string

  /**
   * Override the preset icon size.
   */
  @Prop({ required: false }) iconSize?: number

  /**
   * See `OzContainedButton`.
   */
  @Prop({ default: OzContainedButtonSizePreset.H40px }) sizePreset!: OzContainedButtonSizePreset | null

  /**
   * See `OzContainedButton`.
   */
  @Prop({ default: OzContainedButtonColorScheme.Primary }) colorScheme!: OzContainedButtonColorScheme | null

  /**
   * Dark mode.
   */
  @Prop({ default: 'auto' }) darkMode!: boolean | 'auto'

  /**
   * Use a different icon when the button is active.
   */
  @Prop({ required: false }) activeIconName?: string

  /**
   * Whether the button is disabled.
   */
  @Prop({ default: false }) disabled!: boolean

  /**
   * See `OzContainedButton`.
   */
  @Prop({ required: false }) href?: string

  /**
   * See `OzBaseButton`.
   */
  @Prop({ default: OzContainedButtonHrefMode.Default }) hrefMode?: OzContainedButtonHrefMode

  get isActive(): boolean {
    return this.colorScheme
      ? [OzContainedButtonColorScheme.SecondaryActive, OzContainedButtonColorScheme.SecondaryClearActive].includes(
          this.colorScheme,
        )
      : false
  }

  get presetIconSize(): number {
    switch (this.sizePreset) {
      case OzContainedButtonSizePreset.H40px:
        return 16
      case OzContainedButtonSizePreset.H32px:
        return 20
      case OzContainedButtonSizePreset.H24px:
        return 16
      default:
        return 24
    }
  }
}

export {
  OzContainedButtonColorScheme as OzIconButtonColorScheme,
  OzContainedButtonSizePreset as OzIconButtonSizePreset,
  OzContainedButtonHrefMode as OzIconButtonHrefMode,
}
